import './selectRole.scss'
import { useLocal } from '@/i18n/LocalContext'
import CommonModal from '../CommonModal/CommonModal'
import { useStore } from '@/store'
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import usePlayer from '@/hooks/usePlayer'
import { emitter } from '@/emitter'
import { Handler } from 'mitt'
import { getRoleList } from '@/request/userServer'
import { bind, getBaseData, syncData } from '@/request'
import { logout } from '@/request/utils'
import { closeLoading, loading, toast } from '@/utils'
import grayBtnImg from '@/assets/static-img/anniversary_n_btn_022.png'

export default function SelectRole() {
  const { $t } = useLocal()
  const { state, dispatch } = useStore()
  const userId = state.login.userInfo.userId
  const zoneId = state.login.userInfo.zoneId
  const playerType = state.login.userInfo.playerType
  const justBinded = state.login.userInfo.justBinded
  const playerId = state.login.userInfo.playerId
  const playerName = state.login.userInfo.playerName
  const serverName = state.login.userInfo.serverName
  const isEur = useMemo(() => [1, 2].includes(zoneId), [zoneId])
  const selectRoleOpen = state.modalData.selectRoleOpen
  const { logined, isBinded, checked, isCodeBinded } = usePlayer()
  const [showAreaList, setShowAreaList] = useState(false)
  const [showRoleList, setShowRoleList] = useState(false)
  const [currentAreaName, setCurrentAreaName] = useState('')
  const [currentRole, setCurrentRole] = useState<{
    playerId: string
    playerName: string
    serverId: string
    serverName: string
  } | null>(null)
  const roleConfirmText = useMemo(() => {
    if (!currentRole) return ''

    const currName = `<span>${currentRole.serverName}${currentRole.playerName}</span>`
    return `${$t('请确认是否使用')}${currName}`
  }, [currentRole])
  const btnStyle = useMemo<CSSProperties>(() => {
    const selected = currentRole && !!currentRole.playerId
    return selected
      ? {}
      : {
          backgroundImage: `url(${grayBtnImg})`,
          color: '#e8e8e8',
        }
  }, [currentRole])
  const [roleList, setRoleList] = useState<Role[]>([])
  const getBaseDataPending = useRef(false)

  const _setBaseData = (
    data: Awaited<ReturnType<typeof getBaseData>>,
    playerId: string,
    isBinded: boolean
  ) => {
    dispatch({
      type: 'setBaseData',
      data,
    })

    if (!isBinded) return

    // 同步玩家数据
    syncData(playerId).then((newData) => {
      dispatch({
        type: 'syncData',
        data: {
          friends: newData.list,
          tasks: newData.tasks,
        },
      })
    })
  }

  useEffect(() => {
    const _handle: Handler<CheckPlayerEvent> = (e) => {
      if (!logined) {
        emitter.emit('checkPlayerReturn', false)
        return dispatch({
          type: 'openWebLogin',
          data: {
            openWebLogin: true,
          },
        })
      }

      if (!isBinded) {
        emitter.emit('checkPlayerReturn', false)
        return dispatch({
          type: 'updateModal',
          data: {
            selectRoleOpen: true,
          },
        })
      }

      emitter.emit('checkPlayerReturn', true)

      switch (e.source) {
        case 'account':
          dispatch({
            type: 'setAlertData',
            data: {
              open: true,
              content: `${$t(
                '当前登录角色'
              )}: <span>${serverName}${playerName}</span>`,
              resolveText: $t('关闭'),
              rejectText: !window.inSDK ? $t('退出登录') : '',
              onReject: () => {
                logout($t('确认退出登录？'))
              },
            },
          })
          break

        default:
          break
      }
    }

    const _cancel = () => {
      emitter.off('checkPlayer', _handle)
    }

    emitter.on('checkPlayer', _handle)

    if (checked === 1 && isBinded && !getBaseDataPending.current) {
      getBaseDataPending.current = true
      loading()
      getBaseData(playerId, zoneId)
        .then((data) => _setBaseData(data, playerId, isBinded))
        .finally(() => {
          closeLoading()
          getBaseDataPending.current = false
        })
    }

    // 绑定角色弹窗
    if (logined && checked === 1 && !isBinded) {
      dispatch({
        type: 'updateModal',
        data: {
          selectRoleOpen: true,
        },
      })
      return _cancel
    }

    // 若为回归玩家，则自动弹窗绑定邀请码
    if (isBinded && playerType === 2 && !isCodeBinded && !justBinded) {
      openCodeModal()
    }

    return _cancel
  }, [
    logined,
    checked,
    zoneId,
    isBinded,
    playerType,
    justBinded,
    serverName,
    playerId,
    playerName,
  ])

  // 获取角色列表
  useEffect(() => {
    const _cancel = () => {}

    if (!selectRoleOpen || zoneId === 0) return _cancel

    setAreaFieldName(zoneId)
    setCurrentRole(null)
    getRoleList(zoneId, userId).then((roleList) => {
      setRoleList(roleList)

      const _checkAllRoleList = async () => {
        if (!isEur) return roleList

        const firstZoneId = zoneId as Exclude<Zone['zoneId'], 0 | 3 | 5>
        const secondZoneId = firstZoneId === 1 ? 2 : 1
        const list = await getRoleList(secondZoneId, userId)
        return roleList.concat(list)
      }
      _checkAllRoleList().then((allRoleList) => {
        setRoleList(allRoleList)
        if (allRoleList.length === 0) {
          if (!window.inSDK) {
            dispatch({
              type: 'setAlertData',
              data: {
                open: true,
                content: $t('您的账号无当前大区的角色，请检查！'),
                resolveText: $t('切换账号'),
                rejectText: $t('取消'),
                rejectClose: true,
                onResolve: () => {
                  logout()
                  setTimeout(() => {
                    dispatch({
                      type: 'openWebLogin',
                      data: {
                        openWebLogin: true,
                      },
                    })
                  }, 1)
                },
                onReject: () => {
                  dispatch({
                    type: 'closeModal',
                  })
                },
              },
            })
          } else {
            dispatch({
              type: 'setAlertData',
              data: {
                open: true,
                content: $t('请更换账号登录'),
                resolveText: $t('确定'),
                resolveClose: true,
              },
            })
          }
        }
      })
    })

    return _cancel
  }, [selectRoleOpen, zoneId, userId])

  const toggleAreaList: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
    setShowAreaList((b) => !b)
    if (!showAreaList) {
      setShowRoleList(false)
    }
  }

  const toggleRoleList: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
    setShowRoleList((b) => !b)
    if (!showRoleList) {
      setShowAreaList(false)
    }
  }

  const closeAllList = () => {
    setShowAreaList(false)
    setShowRoleList(false)
  }

  const selectZone = (zoneId: Zone['zoneId']) => {
    setCurrentRole(null)

    setAreaFieldName(zoneId)
    dispatch({
      type: 'setZoneId',
      data: {
        zoneId,
      },
    })
  }

  const selectRole = (role: Role) => {
    setCurrentRole({
      playerId: role.playerId,
      playerName: role.nickname,
      serverId: role.serverId,
      serverName: role.serverName,
    })
  }

  const confirmRole = useCallback(() => {
    if (currentRole == null) return

    dispatch({
      type: 'setAlertData',
      data: {
        open: true,
        content: roleConfirmText,
        hint: $t('*角色一旦绑定，活动期间不可更改'),
        onResolve: () => {
          loading()
          bind(currentRole.playerId)
            .then((playerType) => {
              if (!playerType) return

              dispatch({
                type: 'setPlayer',
                data: {
                  playerType,
                  playerId: currentRole.playerId,
                  playerName: currentRole.playerName,
                  serverId: currentRole.serverId,
                  serverName: currentRole.serverName,
                  isBinded: true,
                },
              })

              dispatch({
                type: 'updateModal',
                data: {
                  selectRoleOpen: false,
                },
              })

              // setTimeout(() => {
              //   dispatch({
              //     type: 'setAlertData',
              //     data: {
              //       open: true,
              //       showLogoutBtn: true,
              //       content: `${$t('当前登录角色')}: ${currentRole.serverName}${
              //         currentRole.playerName
              //       }`,
              //       resolveClose: true,
              //       onResolve: () => {
              //         if (playerType === 2) {
              //           openCodeModal()
              //         }
              //       },
              //     },
              //   })
              // }, 1)
              setTimeout(() => {
                toast($t('绑定成功'), false, 2000)
                setTimeout(() => {
                  if (playerType === 2) {
                    openCodeModal()
                  }
                }, 2000)
              }, 4)
            })
            .finally(closeLoading)
        },
      },
    })
  }, [zoneId, currentRole, roleConfirmText])

  const _logout = () => {
    logout()
  }

  return (
    <CommonModal
      open={selectRoleOpen}
      title={$t('角色绑定')}
      type="select-role"
      onBodyClick={closeAllList}
    >
      {[
        [
          /* isEur ? (<div key="11" className={`region${showAreaList ? ' region--open' : ''}`}>
          <div className="region-field" onClick={toggleAreaList}>
            <span>{!currentAreaName ? $t('请选择大区') : currentAreaName}</span>
            <i className="region-field__arrow"></i>
          </div>
          <div className="region-list">
            <div className="region-item" onClick={() => selectZone(1)}>{$t('美洲')}</div>
            <div className="region-item" onClick={() => selectZone(2)}>{$t('欧洲')}</div>
          </div>
        </div>) : null, */
          <div key="12" className={`role${showRoleList ? ' role--open' : ''}`}>
            <div className="role-field" onClick={toggleRoleList}>
              <span
                dangerouslySetInnerHTML={{
                  __html: !currentRole
                    ? $t('请选择角色')
                    : currentRole.serverName + currentRole.playerName,
                }}
              ></span>
              <i className="role-field__arrow"></i>
            </div>
            <div className="role-list">
              {roleList.map((role) => (
                <div
                  key={role.playerId}
                  className="role-item"
                  onClick={() => selectRole(role)}
                  dangerouslySetInnerHTML={{ __html: role.optionName }}
                ></div>
              ))}
            </div>
          </div>,
          <p
            key="13"
            className="common-modal-text"
            style={{ textAlign: 'center', opacity: !currentRole ? '0' : '1' }}
            dangerouslySetInnerHTML={{ __html: roleConfirmText }}
          ></p>,
          <p key="14" className="common-modal-hint">
            {$t('*角色一旦绑定，活动期间不可更改')}
          </p>,
        ],
        [
          !window.inSDK ? (
            <a
              key="21"
              data-key="logout"
              className="common-modal-reject"
              onClick={_logout}
            >
              <span>{$t('退出登录')}</span>
            </a>
          ) : null,
          <a
            key="22"
            className="common-modal-btn"
            onClick={confirmRole}
            style={btnStyle}
          >
            <span>{$t('确定')}</span>
          </a>,
        ],
      ]}
    </CommonModal>
  )

  function openCodeModal() {
    dispatch({
      type: 'updateModal',
      data: {
        recallOpen: true,
      },
    })
  }

  function setAreaFieldName(zoneId: number) {
    setCurrentAreaName(zoneId === 1 ? $t('美洲') : $t('欧洲'))
  }
}
